import { Component, inject, input, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { FunctionAuthStrategy } from '@vendasta/protos/ai_assistants/v1alpha1';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { CommonModule } from '@angular/common';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { Observable, of, take } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { ConsentAdminApiService } from '@vendasta/sso';
import { IntegrationMarketingApiService, ListIntegrationMarketingRequest } from '@vendasta/platform-integrations';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyTooltip } from '@vendasta/galaxy/tooltip';
import { MatIconModule } from '@angular/material/icon';

interface IntegrationOption {
  integrationType: string;
  displayName: string;
}

// Integrations platform-integrations can make a credentialed outbound call for on the
// tool's behalf — either via the REST passthrough proxy (BaseURL + token) or via MCP
// (mcpServerURL + token) — carry this category key in their integration-config
// definitions. Integrations still serviced by the legacy C# online-integrations service,
// or not yet wired into the proxy, would fail at runtime, so they stay untagged.
const AI_TOOLS_CATEGORY = 'ai_tools';

// A connected-integration tool must be addressable: by a pinned connection ID, or by an
// integration type resolved at runtime. SMB mode never shows the connection ID field, so this
// same rule reduces to "integration required" there.
const connectedIntegrationNeedsIdentifier: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  if (control.get('type')?.value !== 'connectedIntegration') {
    return null;
  }
  const connectionId = (control.get('connectedIntegrationId')?.value ?? '').trim();
  const integrationType = (control.get('integrationType')?.value ?? '').trim();
  return connectionId || integrationType ? null : { connectionRequired: true };
};

type AuthStrategyType =
  | 'unspecified'
  | 'platformManaged'
  | 'impersonation'
  | 'impersonationWithPlatformManagedFallback'
  | 'connectedIntegration';

export type AiToolAuthStrategyFormControls = {
  type: FormControl<string | null>;
  requiredScopes: FormControl<string[] | null>;
  connectedIntegrationId: FormControl<string | null>;
  integrationType: FormControl<string | null>;
};

export class AiToolAuthStrategyForm extends FormGroup<AiToolAuthStrategyFormControls> {
  constructor(authStrategy?: FunctionAuthStrategy) {
    let type: AuthStrategyType = 'unspecified';
    let requiredScopes: string[] = [];
    let connectedIntegrationId = '';
    let integrationType = '';

    if (authStrategy) {
      if (authStrategy.platformManaged) {
        type = 'platformManaged';
        requiredScopes = authStrategy.platformManaged.requiredScopes || [];
      } else if (authStrategy.impersonation) {
        type = 'impersonation';
        requiredScopes = authStrategy.impersonation.requiredScopes || [];
      } else if (authStrategy.impersonationWithPlatformManagedFallback) {
        type = 'impersonationWithPlatformManagedFallback';
        requiredScopes = authStrategy.impersonationWithPlatformManagedFallback.requiredScopes || [];
      } else if (authStrategy.connectedIntegration) {
        type = 'connectedIntegration';
        connectedIntegrationId = authStrategy.connectedIntegration.connectionId || '';
        integrationType = authStrategy.connectedIntegration.integrationType || '';
      }
    }
    super(
      {
        type: new FormControl(type, Validators.required),
        requiredScopes: new FormControl<string[]>(requiredScopes),
        connectedIntegrationId: new FormControl<string>(connectedIntegrationId),
        integrationType: new FormControl<string>(integrationType),
      },
      { validators: connectedIntegrationNeedsIdentifier },
    );
  }

  public get authStrategy(): FunctionAuthStrategy {
    const { type, requiredScopes, connectedIntegrationId, integrationType } = this.getRawValue();
    switch (type) {
      case 'platformManaged':
        return { platformManaged: { requiredScopes: requiredScopes || [] } };
      case 'impersonation':
        return { impersonation: { requiredScopes: requiredScopes || [] } };
      case 'impersonationWithPlatformManagedFallback':
        return { impersonationWithPlatformManagedFallback: { requiredScopes: requiredScopes || [] } };
      case 'connectedIntegration':
        return {
          connectedIntegration: { connectionId: connectedIntegrationId || '', integrationType: integrationType || '' },
        };
      default:
        return { unspecified: {} };
    }
  }
}

@Component({
  selector: 'ai-tool-auth-strategy-form',
  imports: [
    CommonModule,
    GalaxyFormFieldModule,
    MatSelectModule,
    ReactiveFormsModule,
    TranslateModule,
    GalaxyTooltip,
    MatIconModule,
    MatCheckboxModule,
  ],
  templateUrl: './ai-tool-auth-strategy-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './ai-tool-auth-strategy-form.component.scss',
})
export class AiToolAuthStrategyFormComponent {
  form = input.required<AiToolAuthStrategyForm>();
  readonly showScopes = input<boolean>(false);
  readonly connectedIntegrationOnly = input<boolean>(false);
  private scopes = inject(ConsentAdminApiService);
  private integrations = inject(IntegrationMarketingApiService);

  scopeOptions$: Observable<string[]> = this.scopes.listScopes({ pageSize: 1000 }).pipe(
    take(1),
    map((resp) => resp?.scopes?.map((s) => s.id)),
    shareReplay(),
  );

  protected readonly integrationOptions = toSignal(
    this.integrations
      .list(
        new ListIntegrationMarketingRequest({
          pagingOptions: { pageSize: 500 },
          filters: { categoryKeys: [AI_TOOLS_CATEGORY] },
        }),
      )
      .pipe(
        map((resp) =>
          (resp?.integrations ?? [])
            .map((i) => ({ integrationType: i.integrationType, displayName: i.displayName }))
            .sort((a, b) => a.displayName.localeCompare(b.displayName)),
        ),
        catchError(() => of([] as IntegrationOption[])),
      ),
    { initialValue: [] as IntegrationOption[] },
  );

  toggleConnectedIntegration(checked: boolean) {
    this.form().controls.type.setValue(checked ? 'connectedIntegration' : 'unspecified');
  }

  typeChange(type: AuthStrategyType) {
    const requiresScopes =
      type === 'platformManaged' || type === 'impersonation' || type === 'impersonationWithPlatformManagedFallback';
    if (requiresScopes && this.showScopes()) {
      this.form().controls.requiredScopes.setValidators(Validators.required);
    } else {
      this.form().controls.requiredScopes.clearValidators();
    }
    this.form().controls.requiredScopes.updateValueAndValidity();
  }
}
